import Button from '@rio-cloud/rio-uikit/Button';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { useEffect, useMemo, useState } from 'react';

import { useLazyGetPackageActivationsQuery } from '../api/packageActivation/packageActivationApiSlice';
import type { PackageActivation, PackageActivationStatus } from '../api/packageActivation/types';
import { PackageActivationTableToolbar, STATUS_OPTIONS, type StatusOption } from './PackageActivationTableToolbar';
import ReQueuePackageActivationConfirmationDialog from './ReQueuePackageActivationConfirmationDialog';

interface PackageActivationTableRowActionProps {
    packageActivation: PackageActivation;
}

const PackageActivationTableRowAction = (props: PackageActivationTableRowActionProps) => {
    const { accountId, assetId, status, packageCode } = props.packageActivation;
    const enableRequeueAction = status === 'FAILED' || status === 'REJECTED';
    const searchParams = new URLSearchParams({
        accountId,
        resourceIds: Array.isArray(assetId) ? assetId.join(',') : assetId,
    });
    const [showReQueuePackageActivationConfirmationDialog, setShowReQueuePackageActivationConfirmationDialog] =
        useState(false);

    const handleRedirectAction = () => {
        window.open(`/activation/activations?${searchParams.toString()}`, '_blank');
    };

    return (
        <>
            <ReQueuePackageActivationConfirmationDialog
                accountId={accountId}
                assetId={assetId}
                packageCode={packageCode}
                show={showReQueuePackageActivationConfirmationDialog}
                onClickCancel={() => setShowReQueuePackageActivationConfirmationDialog(false)}
                onClickConfirm={() => setShowReQueuePackageActivationConfirmationDialog(false)}
            />

            <ButtonDropdown
                className={'btn-s'}
                dropdownClassName={'overflow-auto'}
                title={<span className='uikit-icon icon-option-vertical' />}
                variant='link'
                iconOnly
                items={[
                    {
                        value: (
                            <div>
                                <span className='uikit-icon icon-search-list margin-right-5 text-size-18' />
                                View activations
                            </div>
                        ),
                        onSelect: handleRedirectAction,
                    },
                    {
                        value: (
                            <div>
                                <span className='uikit-icon icon-refresh margin-right-5 text-size-18' />
                                Requeue
                            </div>
                        ),
                        disabled: !enableRequeueAction,
                        onSelect: () => setShowReQueuePackageActivationConfirmationDialog(true),
                    },
                ]}
            />
        </>
    );
};

const HEADER_MESSAGE_IDS = [
    'Package ID',
    'Account ID',
    'Asset ID',
    'Package Code',
    'Status',
    'Operation Number',
    'Starts',
    'Ends',
    'Last Updated',
    'Succeeded',
    'Retries',
    'Failed Reason',
    'Action',
];

export const PackageActivationTable = () => {
    const [fetchedPackageActivations, setFetchedPackageActivations] = useState<PackageActivation[]>([]);
    const [getPackageActivations, { isLoading, isSuccess, isError, data }] = useLazyGetPackageActivationsQuery();
    const [statusOptions, setStatusOptions] = useState(
        STATUS_OPTIONS.map(option => ({
            ...option,
            selected: false,
        }))
    );
    const [filters, setFilters] = useState<{
        accountId?: string;
        assetId?: string;
        status?: PackageActivationStatus;
    }>({});

    useEffect(() => {
        const accountId = filters.accountId?.trim();
        const assetId = filters.assetId?.trim();

        if (!accountId && !assetId) {
            setFetchedPackageActivations([]);
            return;
        }

        getPackageActivations({
            ...filters,
            accountId: accountId || undefined,
            assetId: assetId || undefined,
        });
    }, [getPackageActivations, filters]);

    useEffect(() => {
        if (import.meta.env?.MODE === 'test') {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        const accountId = params.get('accountId') ?? undefined;
        const assetId = params.get('assetId') ?? undefined;
        const statusParam = (params.get('status') as PackageActivationStatus | 'ALL' | null) ?? null;

        const status = statusParam && statusParam !== 'ALL' ? (statusParam as PackageActivationStatus) : undefined;

        setFilters({
            accountId,
            assetId,
            status,
        });

        setStatusOptions(
            STATUS_OPTIONS.map(option => ({
                ...option,
                selected: statusParam != null ? option.label === statusParam : false,
            }))
        );
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();

        const accountIdTrimmed = filters.accountId?.trim();
        if (accountIdTrimmed) {
            params.set('accountId', accountIdTrimmed);
        }

        const assetIdTrimmed = filters.assetId?.trim();
        if (assetIdTrimmed) {
            params.set('assetId', assetIdTrimmed);
        }

        if (filters.status) {
            params.set('status', filters.status);
        }

        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        if (import.meta.env?.MODE !== 'test') {
            window.history.pushState({}, '', newUrl);
        }
    }, [filters]);

    useEffect(() => {
        if (isSuccess && data) {
            setFetchedPackageActivations(data);
        }
    }, [isSuccess, data]);

    const handleAccountIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilters({ ...filters, accountId: value });
    };

    const handleResourceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilters({ ...filters, assetId: value });
    };

    const handleStatusChange = (item: StatusOption) => {
        setFilters({ ...filters, status: item.label === 'ALL' ? undefined : (item.label as PackageActivationStatus) });

        const updatedStatusOptions = STATUS_OPTIONS.map(option => ({
            ...option,
            selected: option.id === item.id,
        }));
        setStatusOptions(updatedStatusOptions);
    };

    const statusValue = useMemo(() => statusOptions.filter(o => o.selected).map(o => o.id), [statusOptions]);

    return (
        <>
            <PackageActivationTableToolbar
                handleAccountIdChange={handleAccountIdChange}
                handleResourceIdChange={handleResourceIdChange}
                handleStatusChange={handleStatusChange}
                accountId={filters.accountId}
                assetId={filters.assetId}
                statusOptions={statusOptions}
                statusValue={statusValue}
            />
            <div className='table-responsive min-height-350'>
                <table className='table table-hover table-head-filled table-bordered'>
                    <thead>
                        <tr>
                            {HEADER_MESSAGE_IDS.map(id => (
                                <th key={id}>{id}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={HEADER_MESSAGE_IDS.length}>Loading...</td>
                            </tr>
                        ) : isError ? (
                            <tr>
                                <td colSpan={HEADER_MESSAGE_IDS.length}>Error fetching package activations</td>
                            </tr>
                        ) : (
                            fetchedPackageActivations.map(packageActivation => (
                                <tr key={packageActivation.id}>
                                    <td>
                                        <div
                                            className='display-flex justify-content-between align-items-start'
                                            style={{ width: '100%' }}
                                        >
                                            <span className='flex-grow-1'>{packageActivation.id}</span>
                                            <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                    <Tooltip id={`copy-tooltip-${packageActivation.id}`} allowOnTouch>
                                                        Copy
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    bsStyle='muted'
                                                    bsSize='xs'
                                                    className='btn-icon-only'
                                                    onClick={() => navigator.clipboard.writeText(packageActivation.id)}
                                                >
                                                    <span className='uikit-icon icon-clipboard' />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    </td>
                                    <td>
                                        <div
                                            className='display-flex justify-content-between align-items-start'
                                            style={{ width: '100%' }}
                                        >
                                            <span className='flex-grow-1'>
                                                <a
                                                    href={`/activation/activations?accountId=${encodeURIComponent(packageActivation.accountId)}`}
                                                >
                                                    {packageActivation.accountId}
                                                </a>
                                            </span>
                                            <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                    <Tooltip
                                                        id={`copy-tooltip-${packageActivation.accountId}`}
                                                        allowOnTouch
                                                    >
                                                        Copy
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    bsStyle='muted'
                                                    bsSize='xs'
                                                    className='btn-icon-only'
                                                    onClick={() =>
                                                        navigator.clipboard.writeText(packageActivation.accountId)
                                                    }
                                                >
                                                    <span className='uikit-icon icon-clipboard' />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    </td>
                                    <td>
                                        <div
                                            className='display-flex justify-content-between align-items-start'
                                            style={{ width: '100%' }}
                                        >
                                            <span className='flex-grow-1'>
                                                {Array.isArray(packageActivation.assetId) ? (
                                                    <a
                                                        href={`/activation/activations?accountId=${encodeURIComponent(packageActivation.accountId)}&resourceIds=${encodeURIComponent(packageActivation.assetId.join(','))}`}
                                                    >
                                                        {packageActivation.assetId.join(',')}
                                                    </a>
                                                ) : (
                                                    <a
                                                        href={`/activation/activations?accountId=${encodeURIComponent(packageActivation.accountId)}&resourceIds=${encodeURIComponent(packageActivation.assetId)}`}
                                                    >
                                                        {packageActivation.assetId}
                                                    </a>
                                                )}
                                            </span>
                                            <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                    <Tooltip
                                                        id={`copy-tooltip-${Array.isArray(packageActivation.assetId) ? packageActivation.assetId.join(',') : packageActivation.assetId}`}
                                                        allowOnTouch
                                                    >
                                                        Copy
                                                    </Tooltip>
                                                }
                                            >
                                                <Button
                                                    bsStyle='muted'
                                                    bsSize='xs'
                                                    className='btn-icon-only'
                                                    onClick={() =>
                                                        navigator.clipboard.writeText(
                                                            Array.isArray(packageActivation.assetId)
                                                                ? packageActivation.assetId.join(',')
                                                                : packageActivation.assetId
                                                        )
                                                    }
                                                >
                                                    <span className='uikit-icon icon-clipboard' />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    </td>
                                    <td>{packageActivation.packageCode}</td>
                                    <td>{packageActivation.status}</td>
                                    <td>{packageActivation.bookingIntentOperationNumber}</td>
                                    <td>{packageActivation.startsAt}</td>
                                    <td>{packageActivation.endsAt}</td>
                                    <td>{packageActivation.lastUpdatedAt}</td>
                                    <td>{packageActivation.succeededAt ? 'Yes' : 'No'}</td>
                                    <td>{packageActivation.retries}</td>
                                    <td>{packageActivation.failedReason}</td>
                                    <td>
                                        <PackageActivationTableRowAction packageActivation={packageActivation} />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export const PackageActivationTableContainer = PackageActivationTable;
